import SearchService, { SearchServiceOptions } from 'lib/workperks/services/SearchService';
import { API_ROUTES } from 'lib/workperks/common/constants';
import axios from 'axios';
import ADPerkMapper from '../models/ADPerkMapper';
import { ADPerk } from '../models/Perk';
// import Location from '../models/Location';
// import { VenngoRedemptionType } from '../models/RedemptionOptions';

const _isUSPerk = (perk) => {
  if (perk.source !== 'ad' || perk.locations.length === 0) {
    return true;
  }

  let country = perk.locations[0].country;

  if (!country) {
    return true;
  }

  country = country.toLowerCase();
  return !(country === 'canada' || country === 'ca');
};

// FIXME: Do a proper bitmask conversion...
const licenseMap = {
  1: 1, // Venngo-CA
  2: 2, // Venngo-US
  3: 4, // Access-US
};

export const mapUserDataToAccessDevelopment = (user, location) => {
  user.username = user.name;
  if (user.licenses) {
    user.license = user.licenses.map((l) => licenseMap[l]);
  }
  user.site_id = [user.site_id];
  user.locations = [location ? location : user.location];
  delete user.licenses;
  delete user.location;

  return user;
};

const isPresent = (value) => {
  if (!value || value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

export default class ADSearchService extends SearchService {
  public storeKey: any;
  // Here it refers to number of pages.
  public _lastTotalHits: any;
  public restricted: any;
  public source: any;
  page = 1;

  /**
   * Valid API sources.
   * @type {Array}
   * @private
   */
  _validSources = ['ad', 'es'];

  /** Valid sort options
   *  @type {Array}
   *  @private
   */
  _validSorts = ['relevance', 'distance', 'recently_updated', 'alpha', 'random'];

  /** ES to Middleware sort method map */
  _validSortMap = {
    dist: 'distance',
    date: 'recently_updated',
  };

  /** Valid redemption methods */
  _validMethods = ['instore', 'instore_print', 'link', 'call'];

  _validMethodMap = {
    instore:4,
    online:1,
    mobile: 2,
    email:16
  }

  constructor({ user, location, resultSize }: SearchServiceOptions) {
    super({
      user: mapUserDataToAccessDevelopment({ ...user }, location),
      location,
      resultSize,
    });
  }

  protected createPerkMapper() {
    return new ADPerkMapper(this.impressionDescriptor());
  }

  setOffset(offset) {
    if (!this._lastTotalHits) {
      this.page = 1;
      return this;
    }

    // Added 1 because if offset = resultSize, It goes to page 1 again instead of going to page 2
    this.page = Math.floor(offset / this.resultSize) + 1;
    // We cannot have a page 0.
    this.page = this.page <= 0 ? 1 : this.page;

    return this;
  }

  /**
   * Sets whether perks returned are always restricted perks.
   * @param {boolean} isRestricted     True if it always should be restricted, false otherwise.
   * @returns {ADSearchService}
   */
   setRestricted(isRestricted) {
    if (!isRestricted) return this;

    if (typeof isRestricted !== 'boolean') throw 'TypeError: Could not set non boolean value for restriction.';

    this.restricted = isRestricted;

    return this;
  }

  /**
   * Sets the API source to get perks from
   * @param {string} source Source API to pull from, valid according to `_validSources`.
   * @returns {ADSearchService}
   */
  setSource(source) {
    if (!(this._validSources.indexOf(source) > -1))
      throw `InvalidSourceError: The source API specified '${JSON.stringify(source)}' is not a valid source.`;

    this.source = source;

    return this;
  }

  setParentCategory(categoryId) {
    if(categoryId){
      this.categoryId = categoryId instanceof Array ? categoryId : [categoryId];
    }
    return this;
  }

  setSubCategory(subcategoryId) {
    if (subcategoryId.length >= 1) {
      this.subcategoryId = subcategoryId instanceof Array ? subcategoryId : [subcategoryId];
    }
    else {
      this.subcategoryId = null;
    }
    return this;
  }

  /**
   * Gets perks to be used as banners.
   * @returns {Promise}  Promise with all the perks as banners.
   */
  _getPerksForBanner() {
    const requestParameters: any = {};

    requestParameters.query = this.query;
    requestParameters.user = this.user;
    if (this.categoryId) {
      requestParameters.category = this.categoryId;
    }
    requestParameters.random = true;
    requestParameters.tier = 1;
    requestParameters.banners = true;

    if (this.source) requestParameters.source = this.source;

    if (this.categoryId && this.categoryId.length > 0) requestParameters.category = this.categoryId;

    requestParameters.result_size = this.resultSize;

    if (this.location) {
      requestParameters.latitude = this.location.latitude;
      requestParameters.longitude = this.location.longitude;
    }

    if (this.distance) {
      requestParameters.distance = this.distance > 0 ? this.distance : 10000;
    } else {
      if (this.location) requestParameters.distance = 10000;
    }

    return this._requestPerksFromMiddleware(requestParameters);
  }

  /**
   * Gets perks filtered by query and filters set by object.
   * @param   {string}  query Keyword query to filter.
   * @returns {Promise}  Promise with all the perks filtered.
   */
  _getPerksByQuery(query) {
    let requestParameters: any = {};

    if (query) {
      requestParameters.query = `*${query}*`;
    }

    requestParameters = this._buildRequest(requestParameters);

    return this._requestPerksFromMiddleware(requestParameters);
  }

  /**
   * Gets perks filtered by a given store key.
   * @param   {string}  storeKey Store key to filter for perks by.
   * @returns {Promise}  Promise with all the perks for the store key.
   */
   _getPerksByStoreKey(storeKey) {
    const requestParameters: any = {};

    requestParameters.user = this.user;

    if (this.location) {
      requestParameters.latitude = this.location.latitude;
      requestParameters.longitude = this.location.longitude;
      requestParameters.distance = 10000;
    }

    requestParameters.result_size = this.resultSize;
    requestParameters.store_key = storeKey;
    requestParameters.sort = 'distance';

    return this._requestPerksFromMiddleware(requestParameters);
  }

  /**
   * Gets perks filtered by an array of perk IDs.
   *
   * @param   {array}  ids Array of perk IDs to filter by.
   * @returns {Promise}    Promise with all the perks for given IDs.
   */
  _getPerksByIDs(ids) {
    if (!(ids instanceof Array)) {
      throw `InputError: Given IDs '${JSON.stringify(ids)}' are not of type Array.`;
    }

    const requestParameters: any = {};
    requestParameters.user = this.user;
    requestParameters.result_size = this.resultSize;
    requestParameters.sort = this.sort;
    requestParameters.ids = ids;
    requestParameters.result_size = this.resultSize;
    requestParameters.page = this.page;

    return this._requestPerksFromMiddleware(requestParameters);
  }

  /**
   * Get perk by perk ID. Returns all the details needed for the perk page.
   * @param   {string}  id ID of the perk to get details for.
   * @returns {Promise}  Promise when the perk is loaded.
   */
  _getPerkByID(id) {
    const parameters = {
      user: this.user,
    };

    return this._requestPerksFromMiddleware(parameters, id);
  }

  _getPerksByTemplate() {
    let request = this.template;

    request = this._buildRequest(request);

    return this._requestPerksFromMiddleware(request);
  }

  _buildRequest(requestParameters) {
    requestParameters.user = this.user;
    requestParameters.restrictedAccounts = window.s.site.restrictedAccounts;

    if (this.categoryId && this.categoryId.length > 0) requestParameters.category = this.categoryId;

    if (this.subcategoryId && this.subcategoryId.length > 0) {
      requestParameters.subcategory = this.subcategoryId;
    } else {
      delete requestParameters.subcategory;
    }

    if (this.sort) requestParameters.sort = this.sort;

    if (this.tier) requestParameters.tier = this.tier;

    if (this.showNew) {
      requestParameters.new = this.showNew;
    } else {
      delete requestParameters['new'];
    }

    if (this.showExpiringSoon) {
      requestParameters.expiring_soon = this.showExpiringSoon;
    } else {
      delete requestParameters['expiring_soon'];
    }

    if (this.location) {
      requestParameters.latitude = this.location.latitude;
      requestParameters.longitude = this.location.longitude;
    }

    if (this.distance) {
      requestParameters.distance = this.distance > 0 ? this.distance : 10000;
    } else {
      if (this.location) requestParameters.distance = 10000;
    }

    if (this.source) requestParameters.source = this.source;

    if (this.restricted) requestParameters.restricted = this.restricted;

    if (this.redemptionType && this.redemptionType.length > 0) {
      requestParameters.redemption_type = this.redemptionType;
    } else {
      delete requestParameters['redemption_type'];
    }

    if (this.visibleLocations) requestParameters.visibleLocations = this.visibleLocations;

    requestParameters.result_size = this.resultSize;
    requestParameters.page = this.page;

    return requestParameters;
  }

  search(query?) {
    super.search(query);
    // console.log("ADSearchService@search --- isPresent(this.banner) && this.banner === true", isPresent(this.banner) && this.banner === true);
    // console.log("ADSearchService@search --- isPresent(this.ids)", isPresent(this.ids));
    // console.log("ADSearchService@search --- isPresent(this.template)", isPresent(this.template));
    switch (true) {
      case isPresent(this.banner) && this.banner === true:
        return this._getPerksForBanner();
      case isPresent(this.ids):
        return this.ids.length > 1 ? this._getPerksByIDs(this.ids) : this._getPerkByID(this.ids[0]);
      case isPresent(this.template):
        return this._getPerksByTemplate();
    }
    // console.log("ADSearchService@search --- _getPerksByQuery --", this._getPerksByQuery(this.query));
    return this._getPerksByQuery(this.query);
  }

  getPerkDetails() {
    const requestParameters: any = {};
    requestParameters.user = this.user;
    requestParameters.result_size = this.resultSize;
    requestParameters.sort = this.sort;
    requestParameters.ids = this.ids.length > 1 ? this.ids : this.ids[0];
    requestParameters.result_size = this.resultSize;
    requestParameters.page = this.page;

    return this._requestPerksFromMiddleware(requestParameters, requestParameters.ids);
  }

  getAccounts(ids) {
    return this._getPerksByIDs(ids).then(perks => {
      const accounts = [];
      let index = 0;
      perks.forEach(perk => {
        accounts.push({
          id: perk.id,
          companyName: perk.title,
          accountLogo: {
            en: perk.images.logo,
            fr: perk.images.logo,
          },
          source: 'ad',
          position: index++,
          groupID: perk.groupID
        });
      });
      return accounts;
    });
  }

  hasMoreResults() {
    if (!this._lastTotalHits) {
      return false;
    }

    return this.page < this._lastTotalHits;
  }

  next() {
    if (!this.hasMoreResults()) {
      return Promise.reject();
    }

    this.page++;
    return this.search();
  }

  setRedemptionType(redemptionType) {
    if(redemptionType.length > 0) {
      redemptionType = redemptionType.map(redemption => {
        return this._validMethodMap[redemption];
      });
    }

    this.redemptionType = redemptionType;
    return this;
  }

  /**
   * Request method that makes the AJAX call according to specified parameters for perk data.
   * @param  parameters Parameters to send to the middleware perk endpoint.
   */
  async _requestPerksFromMiddleware(parameters, endpoint?): Promise<ADPerk[]> {
    let url = endpoint ? `/perk/${endpoint}` : '/perk';
    url = API_ROUTES.accessdevelopment.endpoint + url;

    const queryHeaders = {
      headers: {
        'Content-Type': 'application/json',
      }
    };
    delete axios.defaults.headers.common['X-Requested-With'];
    delete axios.defaults.headers.common['X-CSRF-TOKEN'];
    
    const { data } = await axios.post(url, parameters, queryHeaders);

    this._lastTotalHits = data.count.total;
    return data.perks.map(this.perkMapper.map);
  }

  /**
   * Filter out any perk with a Canadian location.
   */
   _filterCanadianPerks(data) {
    if (data.perk) {
      data.perk = _isUSPerk(data.perk) ? data.perk : {};
    }

    if (data.perks && data.perks.length > 0) {
      data.perks = data.perks.filter(_isUSPerk);
    }

    return data;
  }

  _getPerkForCategoryTile(categoryId) {
      return {
          random: true,
          // FIXME: Proper Category Mapping Needed oon Middleware
          category: [categoryId]
      };
  }
}
